import { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { Form, Radio } from 'antd';
import styled from 'styled-components';

import { ButtonIw } from '@/_pages/interwill/components/ButtonIw';
import { ButtonIwText } from '@/_pages/interwill/components/ButtonIwText';
import { FieldContainerIw } from '@/_pages/interwill/components/FieldContainerIw';
import { Input } from '@/shared/components';
import { SelectCountry } from '@/shared/components/forms/fields/SelectCountry';
import { SubstanceDetailsFormType } from '@/shared/lib/forms/personal-IDs/substance';
import { CollectionName } from '@/shared/lib/sj-orm/constants';
import { useSJDatabase } from '@/shared/lib/sj-orm/hooks/use-sj-database';
import { DocumentType } from '@/shared/lib/sj-orm/models/document/document-base.dto';
import {
  PersonalIdentifierType,
  SubstanceDto,
} from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { PersonalIdentifierDocumentDto } from '@/shared/lib/sj-orm/models/personalIdentifiersDocuments/personalIdentifiersDocuments.dto';
import { useKeyPairStore } from '@/shared/store/decrypted-keypair.store';
import { Icon } from '@/shared/uikit/Icon';
import { useModalsStore } from '@/widgets/modals/modals.store';
import { ResidenceModalProps } from '@/widgets/modals/types';

import { ModalContainerV1 } from '../containers/ModalContainerV1';
import { InterwillStyledForm } from '../CreateEntityModal/styled';

import { ResidenceParams } from './types';

export const ResidenceModal: FC<ResidenceModalProps> = ({
  addNew,
  residenceIndex,
  beneficiaryData,
  updateBeneficiaryData,
}) => {
  const { t } = useTranslation('widgets/modals', { keyPrefix: 'residence' });

  const modals = useModalsStore();

  const { keyPair } = useKeyPairStore();
  const sjDatabase = useSJDatabase();
  const residence = Number.isInteger(residenceIndex)
    ? beneficiaryData.otherResidences?.[residenceIndex!]
    : beneficiaryData.primaryResidence;

  const estates = sjDatabase.collections.personalIdentifiersDocuments?.findMany(
    (item) =>
      item.beneficiaryId === (beneficiaryData.id || 'me') &&
      item.personalIdentifierType === PersonalIdentifierType.SUBSTANCE &&
      ![...(beneficiaryData.otherResidences || []), beneficiaryData.primaryResidence].find(
        (n) => n?.id === item.id && residence?.id !== n.id,
      ),
  ) as (PersonalIdentifierDocumentDto & SubstanceDetailsFormType)[] | undefined;

  const [tab, setTab] = useState(residence?.country ? 1 : 0);
  const {
    control,
    handleSubmit: formHandleSubmit,
    formState: { isSubmitting },
    watch,
    setValue,
  } = useForm<ResidenceParams>({
    defaultValues: {
      id: residence?.id,
      area: residence?.area,
      apt: residence?.apt,
      firstName: beneficiaryData?.firstName ?? '',
      lastName: beneficiaryData?.lastName ?? '',
    },
  });

  const selectedEstateId = watch('id');
  const selectedEstate = estates?.find((item) => item.id === selectedEstateId);

  useEffect(() => {
    if (estates?.length === 0 || addNew) setTab(2);
  }, [estates, addNew]);

  useEffect(() => {
    if (!selectedEstate) return;
    if (selectedEstate.id !== residence?.id) {
      setValue('area', '');
      setValue('apt', '');
    } else {
      setValue('area', residence?.area ?? '');
      setValue('apt', residence?.apt ?? '');
    }
    setValue('country', selectedEstate.country);
    setValue('city', selectedEstate.address?.city ?? '');
    setValue('street', selectedEstate.address?.street ?? '');
  }, [selectedEstate]);

  const onSubmit = async (values: ResidenceParams) => {
    let id = sjDatabase.generateId(CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS);
    if (tab === 2) {
      await sjDatabase.collections.personalIdentifiersDocuments?.create(
        {
          id,
          type: DocumentType.PERSONAL_IDENTIFIERS,
          personalIdentifierType: PersonalIdentifierType.SUBSTANCE,
          country: values.country,
          city: values.city,
          streetBuilding: values.street,
          name: values.documentName,
          beneficiaryId: beneficiaryData.id || 'me',
          names: values.firstName,
          surnames: values.lastName,
        } as SubstanceDto,
        [keyPair?.publicKey || ''],
      );
    } else {
      await sjDatabase.collections.personalIdentifiersDocuments?.update({
        ...selectedEstate,
        city: values.city,
        streetBuilding: values.street,
        country: values.country,
      } as SubstanceDto);
      id = values.id;
    }

    if (Number.isInteger(residenceIndex)) {
      const residences = [...(beneficiaryData.otherResidences || [])];
      residences[residenceIndex!] = {
        id,
        apt: values.apt,
        city: values.city,
        area: values.area,
        country: values.country,
        streetBuilding: values.street,
        tax: residences[residenceIndex!]?.tax ?? '',
      };
      updateBeneficiaryData({ otherResidences: residences });
    } else {
      updateBeneficiaryData({
        primaryResidence: {
          id,
          apt: values.apt,
          city: values.city,
          area: values.area,
          country: values.country,
          streetBuilding: values.street,
          tax: '',
        },
      });
    }
    modals.closeModal();
  };

  return (
    <ModalContainerV1 title={t('title')}>
      <InterwillStyledForm onFinish={formHandleSubmit(onSubmit)}>
        <InnerForm>
          {tab === 0 && (
            <>
              <Radio.Group
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
                options={estates?.map((item) => ({
                  label: item.name,
                  value: item.id,
                }))}
                value={selectedEstateId}
                onChange={(val) => {
                  setValue('id', val.target.value);
                }}
              />
              <ButtonIwText
                style={{ width: 'fit-content' }}
                icon={<Icon name="plus" size="s20" />}
                onClick={() => setTab(2)}
              >
                {t('addNewBtn')}
              </ButtonIwText>
            </>
          )}

          {tab > 0 && (
            <Controller
              name={'country'}
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                return (
                  <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                    <Form.Item
                      validateStatus={fieldState.error ? 'error' : ''}
                      help={fieldState.error?.message}
                    >
                      <SelectCountry
                        placeholder={t(`fields.${field.name}.placeholder`)}
                        {...field}
                      />
                    </Form.Item>
                  </FieldContainerIw>
                );
              }}
            />
          )}
          {tab === 2 && (
            <>
              <Controller
                name={'documentName'}
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => {
                  return (
                    <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                      <Form.Item
                        validateStatus={fieldState.error ? 'error' : ''}
                        help={fieldState.error?.message}
                      >
                        <Input
                          placeholder={t(`fields.${field.name}.placeholder`)}
                          type={'text'}
                          {...field}
                        />
                      </Form.Item>
                    </FieldContainerIw>
                  );
                }}
              />
              <Row>
                <Controller
                  name={'lastName'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
                <Controller
                  name={'firstName'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
              </Row>
            </>
          )}

          {tab > 0 && (
            <>
              <Row>
                <Controller
                  name={'area'}
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
                <Controller
                  name={'city'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
              </Row>

              <Row>
                <Controller
                  name={'street'}
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)} required>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
                <Controller
                  name={'apt'}
                  control={control}
                  render={({ field, fieldState }) => {
                    return (
                      <FieldContainerIw label={t(`fields.${field.name}.label`)}>
                        <Form.Item
                          validateStatus={fieldState.error ? 'error' : ''}
                          help={fieldState.error?.message}
                        >
                          <Input
                            placeholder={t(`fields.${field.name}.placeholder`)}
                            type={'text'}
                            {...field}
                          />
                        </Form.Item>
                      </FieldContainerIw>
                    );
                  }}
                />
              </Row>
              {tab === 1 && (
                <ButtonIwText
                  style={{ width: 'fit-content' }}
                  onClick={() => {
                    if (estates?.length === 1) {
                      modals.closeModal();
                      modals.openModal({
                        type: 'ReplaceResidenceModal',
                        beneficiaryData,
                        updateBeneficiaryData,
                      });
                      return;
                    }
                    setTab(0);
                  }}
                >
                  {t('replaceBtn')}
                </ButtonIwText>
              )}
            </>
          )}
        </InnerForm>
        <Buttons>
          <ButtonIw loading={isSubmitting} disabled={isSubmitting} htmlType="submit">
            {t('btn')}
          </ButtonIw>
        </Buttons>
      </InterwillStyledForm>
    </ModalContainerV1>
  );
};

const InnerForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: auto;
  padding: 0 2px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 40px;
`;
